import { useState } from 'react';
import { z } from 'zod';
import { productUpdateSchema } from '../schemas/product';

/**
 * @typedef {Object} ValidationError
 * @property {string[]} path
 * @property {string} message
 */

/**
 * Hook for managing form data and validation
 * @template T
 * @param {T} initialData - Initial form data
 * @param {z.ZodType<T>} schema - Zod schema for validation
 * @returns {{
 *   data: T,
 *   errors: ValidationError[],
 *   setData: (data: T) => void,
 *   setFieldValue: (field: keyof T, value: any) => void,
 *   validate: () => boolean,
 *   clearErrors: () => void
 * }}
 */
export function useProductForm(initialData, schema) {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState([]);

  const validate = () => {
    const result = schema.safeParse(data);
    if (!result.success) {
      setErrors(
        result.error.errors.map(error => ({
          path: error.path,
          message: error.message
        }))
      );
      return false;
    }
    setErrors([]);
    return true;
  };

  const setFieldValue = (field, value) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors for this field
    setErrors(prev => prev.filter(error => error.path[0] !== field));
  };

  const clearErrors = () => {
    setErrors([]);
  };

  return {
    data,
    errors,
    setData,
    setFieldValue,
    validate,
    clearErrors
  };
} 